import { createConsumer } from '@rails/actioncable';

// NOTICE: KAPTION-INTEGRATION — Was 20000. The server's presence TTL
// (OnlineStatusTracker::PRESENCE_DURATION) is also 20s, so a heartbeat at the
// same cadence has zero margin: a single delayed tick — routine in a
// backgrounded tab, since browsers throttle setTimeout there — lets the
// Redis presence entry expire and the user flips to "offline" until the next
// beat lands. 8s gives a ~2.5x safety margin against the TTL.
const PRESENCE_INTERVAL = 8000;
const RECONNECT_INTERVAL = 1000;
// NOTICE: KAPTION-INTEGRATION — Increase stale threshold so background tabs
// don't trigger a visible reconnection flash when the user returns.
const STALE_THRESHOLD = 30; // seconds (default is 6)
const DISCONNECT_DEBOUNCE = 3000; // ms — suppress short disconnection UI

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);

    // Increase tolerance for missed pings (default: 6s = 2 missed pings)
    const { monitor } = this.consumer.connection;
    monitor.constructor.staleThreshold = STALE_THRESHOLD;

    this.disconnectDebounceTimer = null;
    this.setupVisibilityReconnect();

    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.initReconnectTimer();
          // Debounce the UI notification so short reconnections are invisible
          this.disconnectDebounceTimer = setTimeout(() => {
            if (BaseActionCableConnector.isDisconnected) {
              this.onDisconnected();
            }
          }, DISCONNECT_DEBOUNCE);
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      clearTimeout(this.disconnectDebounceTimer);
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  setupVisibilityReconnect() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      // Proactively reopen on tab return — don't wait for ConnectionMonitor poll
      const { connection } = this.consumer;
      if (!connection.isOpen()) {
        connection.reopen();
      }
      // NOTICE: KAPTION-INTEGRATION — Refresh presence immediately on tab
      // return instead of waiting for the next PRESENCE_INTERVAL tick. A tab
      // backgrounded long enough (Chrome's intensive timer throttling) can
      // lag the heartbeat well past the server's presence TTL even at 8s, so
      // without this a user could still show "offline" for a beat after
      // switching back.
      this.subscription.updatePresence();
    });
  }

  disconnect() {
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
